import type { CustomAttributeListOutput } from "@orsane/schema/tasks";
import type { ColumnDef } from "@tanstack/react-table";
import {
    Hash,
    MoreHorizontal,
    Pencil,
    SquareDashedMousePointer,
    SquareGanttChart,
    Timer,
    Trash2,
    Type,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ActionsProps {
    attribute: CustomAttributeListOutput;
    onEdit: (attribute: CustomAttributeListOutput) => void;
    onDelete: (id: number) => void;
}

const ActionsCell = ({ attribute, onEdit, onDelete }: ActionsProps) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                }
            ></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(attribute)}>
                    <Pencil />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(attribute.id)}>
                    <Trash2 />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
        text: "Text",
        number: "Number",
        select_one: "Dropdown",
        timer: "Timer",
        orbit_support_project: "Orbit Support Project",
    };
    return typeLabels[type] || type;
};

export const createAttributesColumns = (
    onEdit: (attribute: CustomAttributeListOutput) => void,
    onDelete: (id: number) => void
): ColumnDef<CustomAttributeListOutput>[] => [
    {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
            return (
                <div className="font-medium flex items-center gap-2">
                    {row.original.type === "orbit_support_project" && <SquareGanttChart className="size-3.5" />}
                    {row.original.type === "timer" && <Timer className="size-3.5" />}
                    {row.original.type === "number" && <Hash className="size-3.5" />}
                    {row.original.type === "select_one" && <SquareDashedMousePointer className="size-3.5" />}
                    {row.original.type === "text" && <Type className="size-3.5" />}
                    {row.getValue("name")}
                </div>
            );
        },
    },
    {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
            const type = row.getValue("type") as string;
            return getTypeLabel(type);
        },
    },
    {
        id: "actions",
        enableHiding: false,
        meta: {
            align: "right",
            width: 100,
        },
        cell: ({ row }) => {
            return <ActionsCell attribute={row.original} onEdit={onEdit} onDelete={onDelete} />;
        },
    },
];
